import React, { useState } from "react";
import { Link } from "react-router-dom";
import { DateFormat } from "sr/helpers/functions/DateFormat";

type Mode = "payer" | "receiver";

type Props = {
  rows: any[];
  mode: Mode;
  workorderLinkBase?: string;
  titleLabel?: string;
  titleColumnClassName?: string;
  amountColumnClassName?: string;
  feeColumnClassName?: string;
  totalColumnClassName?: string;
  statusColumnClassName?: string;
  paymentDateColumnClassName?: string;
  tableLayout?: "fixed" | "auto";
  disableHorizontalScroll?: boolean;
};

const getWorkorderDetail = (tx: any) => {
  const raw = tx?.workorders_detail;
  if (!raw) return null;
  return Array.isArray(raw) ? raw[0] : raw;
};

const getIndividualJobDetail = (tx: any) => {
  const raw = tx?.individual_job_detail;
  if (!raw) return null;
  return Array.isArray(raw) ? raw[0] : raw;
};

const getJobTitle = (tx: any) => {
  const workorder = getWorkorderDetail(tx);
  const individualJob = getIndividualJobDetail(tx);
  return workorder?.title || individualJob?.title || "—";
};

const getTitleLink = (tx: any, workorderLinkBase?: string) => {
  if (!workorderLinkBase || !tx?.job_id) return null;
  return getWorkorderDetail(tx) ? `${workorderLinkBase}/${tx.job_id}` : null;
};

const getStatusPillClass = (rawStatus?: string) => {
  const normalized = String(rawStatus || "").toLowerCase();
  if (["transferred", "completed"].includes(normalized)) {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  if (["cancelled"].includes(normalized)) {
    return "bg-rose-50 text-rose-700 border-rose-200";
  }
  if (["hold", "captured"].includes(normalized)) {
    return "bg-amber-50 text-amber-800 border-amber-200";
  }
  if (["requested"].includes(normalized)) {
    return "bg-sky-50 text-sky-700 border-sky-200";
  }
  return "bg-white text-neutral-700 border-neutral-200";
};

const money = (n: any) => `$${Number(n || 0).toFixed(2)}`;

type ReceiptModalProps = {
  isOpen: boolean;
  onClose: () => void;
  transaction: any;
  mode: Mode;
};

const ReceiptModal = ({
  isOpen,
  onClose,
  transaction,
  mode,
}: ReceiptModalProps) => {
  if (!isOpen) return null;

  const serviceFeeField =
    mode === "receiver" ? "job_seeker_fee_amount" : "job_provider_fee_amount";
  const totalField =
    mode === "receiver"
      ? "job_seeker_received_amount"
      : "job_provider_paid_amount";
  const totalLabel = mode === "receiver" ? "You receive" : "Total paid";

  const workorder = getWorkorderDetail(transaction);
  const individualJob = getIndividualJobDetail(transaction);

  const payType = workorder?.pay_type || individualJob?.pay_type;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-neutral-900">
              Receipt Details
            </h2>
            <button
              onClick={onClose}
              className="text-neutral-500 hover:text-neutral-700 transition-colors"
              aria-label="Close"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Transaction ID */}
          <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
            <div className="text-xs text-neutral-600 mb-1">Transaction ID</div>
            <div className="text-sm font-mono text-neutral-900 break-all">
              {transaction?.transaction_id || transaction?._id || "—"}
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-neutral-600">Status</span>
            <span
              className={`inline-flex items-center h-7 px-3 rounded-md border text-xs font-semibold capitalize ${getStatusPillClass(
                transaction?.status,
              )}`}
            >
              {transaction?.status || "—"}
            </span>
          </div>

          {/* Job Details */}
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Job Title</span>
              <span className="text-sm font-medium text-neutral-900 text-right max-w-[60%] break-words">
                {getJobTitle(transaction)}
              </span>
            </div>

            {workorder?.customer_name && (
              <div className="flex justify-between">
                <span className="text-sm text-neutral-600">Customer</span>
                <span className="text-sm font-medium text-neutral-900">
                  {workorder.customer_name}
                </span>
              </div>
            )}

            {(workorder?.location || individualJob?.location) && (
              <div className="flex justify-between">
                <span className="text-sm text-neutral-600">Location</span>
                <span className="text-sm font-medium text-neutral-900 text-right max-w-[60%] break-words">
                  {workorder?.location || individualJob?.location}
                </span>
              </div>
            )}

            {payType && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-600">Pay Type</span>
                <span className="inline-flex items-center h-6 px-3 rounded-md border text-xs font-semibold bg-blue-50 text-blue-700 border-blue-200">
                  {payType}
                </span>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-neutral-200"></div>

          {/* Payment Breakdown */}
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Amount</span>
              <span className="text-sm font-medium text-neutral-900 tabular-nums">
                {money(transaction?.amount)}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Service Fee</span>
              <span className="text-sm font-medium text-neutral-900 tabular-nums">
                {money(transaction?.[serviceFeeField])}
              </span>
            </div>

            <div className="border-t border-neutral-200 pt-3">
              <div className="flex justify-between">
                <span className="text-base font-semibold text-neutral-900">
                  {totalLabel}
                </span>
                <span className="text-base font-semibold text-neutral-900 tabular-nums">
                  {money(transaction?.[totalField])}
                </span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-neutral-200"></div>

          {/* Date Information */}
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Payment Date</span>
              <span className="text-sm font-medium text-neutral-900">
                {transaction?.payment_date
                  ? DateFormat(transaction?.payment_date)
                  : "—"}
              </span>
            </div>

            {transaction?.created_at && (
              <div className="flex justify-between">
                <span className="text-sm text-neutral-600">Created Date</span>
                <span className="text-sm font-medium text-neutral-900">
                  {DateFormat(transaction?.created_at)}
                </span>
              </div>
            )}
          </div>

          {/* Additional Details */}
          {transaction?.description && (
            <>
              <div className="border-t border-neutral-200"></div>
              <div>
                <div className="text-sm text-neutral-600 mb-2">Description</div>
                <div className="text-sm text-neutral-900 bg-neutral-50 rounded-lg p-3 border border-neutral-200">
                  {transaction.description}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-neutral-50 border-t border-neutral-200 px-6 py-4 rounded-b-xl">
          <button
            onClick={onClose}
            className="w-full bg-[#0078ae] hover:bg-[#006699] text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const TransactionsTable = ({
  rows,
  mode,
  workorderLinkBase,
  titleLabel = "Title",
  titleColumnClassName,
  amountColumnClassName,
  feeColumnClassName,
  totalColumnClassName,
  statusColumnClassName,
  paymentDateColumnClassName,
  tableLayout = "fixed",
  disableHorizontalScroll = false,
}: Props) => {
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const serviceFeeField =
    mode === "receiver" ? "job_seeker_fee_amount" : "job_provider_fee_amount";
  const totalField =
    mode === "receiver"
      ? "job_seeker_received_amount"
      : "job_provider_paid_amount";
  const totalLabel = mode === "receiver" ? "You receive" : "Total paid";

  const amountW = amountColumnClassName || "w-[120px]";
  const feeW = feeColumnClassName || "w-[120px]";
  const totalW = totalColumnClassName || "w-[130px]";
  const statusW = statusColumnClassName || "w-[140px]";
  const dateW = paymentDateColumnClassName || "w-[140px]";

  const handleViewReceipt = (transaction: any) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };

  return (
    <>
      <div
        className={`hidden lg:block mt-4 ${
          disableHorizontalScroll ? "" : "overflow-x-auto"
        }`}
      >
        <table
          className={`w-full text-left ${
            tableLayout === "auto" ? "table-auto" : "table-fixed"
          }`}
        >
          <thead className="border-b border-neutral-200 text-sm text-neutral-700">
            <tr>
              <th
                className={`py-3 px-4 font-semibold ${
                  titleColumnClassName || ""
                }`}
              >
                {titleLabel}
              </th>
              <th className={`py-3 px-4 font-semibold ${amountW}`}>Amount</th>
              <th className={`py-3 px-4 font-semibold ${feeW}`}>Service fee</th>
              <th className={`py-3 px-4 font-semibold ${totalW}`}>
                {totalLabel}
              </th>
              <th className={`py-3 px-4 font-semibold ${statusW}`}>Status</th>
              <th className={`py-3 px-4 font-semibold ${dateW}`}>
                Payment date
              </th>
              <th className="py-3 px-4 font-semibold w-[100px]">Action</th>
            </tr>
          </thead>
          <tbody className="text-sm text-neutral-800">
            {rows.length === 0 ? (
              <tr>
                <td className="py-6 px-4 text-neutral-500" colSpan={7}>
                  No transactions found.
                </td>
              </tr>
            ) : (
              rows.map((tx: any, idx: number) => {
                const titleLink = getTitleLink(tx, workorderLinkBase);
                const key = tx?._id ?? `${tx?.transaction_id ?? "tx"}-${idx}`;
                return (
                  <tr
                    key={key}
                    className="border-b border-neutral-100 last:border-b-0"
                  >
                    <td
                      className={`py-3 px-4 whitespace-normal break-words ${
                        titleColumnClassName || ""
                      }`}
                    >
                      {titleLink ? (
                        <Link
                          to={titleLink}
                          className="text-[#0078ae] font-medium hover:underline break-words whitespace-normal"
                        >
                          {getJobTitle(tx)}
                        </Link>
                      ) : (
                        getJobTitle(tx)
                      )}
                    </td>
                    <td className="py-3 px-4 tabular-nums whitespace-nowrap">
                      {money(tx?.amount)}
                    </td>
                    <td className="py-3 px-4 tabular-nums whitespace-nowrap">
                      {money(tx?.[serviceFeeField])}
                    </td>
                    <td className="py-3 px-4 tabular-nums whitespace-nowrap">
                      {money(tx?.[totalField])}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center h-6 px-3 rounded-md border text-xs font-semibold capitalize ${getStatusPillClass(
                          tx?.status,
                        )}`}
                      >
                        {tx?.status || "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      {tx?.payment_date ? DateFormat(tx?.payment_date) : "—"}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewReceipt(tx)}
                        className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-[#0078ae] bg-[#0078ae]/10 hover:bg-[#0078ae]/20 rounded-md transition-colors"
                        aria-label="View receipt"
                      >
                        <svg
                          className="w-4 h-4 mr-1.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        View
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="lg:hidden mt-4 space-y-3">
        {rows.length === 0 ? (
          <div className="py-6 text-sm text-neutral-500">
            No transactions found.
          </div>
        ) : (
          rows.map((tx: any, idx: number) => {
            const titleLink = getTitleLink(tx, workorderLinkBase);
            const key = tx?._id ?? `${tx?.transaction_id ?? "tx"}-${idx}`;
            return (
              <div
                key={key}
                className="border border-neutral-200 rounded-lg p-4 bg-white"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs text-neutral-600 mt-0.5 whitespace-normal break-words">
                      {titleLink ? (
                        <Link
                          to={titleLink}
                          className="text-[#0078ae] font-medium hover:underline break-words whitespace-normal"
                        >
                          {getJobTitle(tx)}
                        </Link>
                      ) : (
                        getJobTitle(tx)
                      )}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 inline-flex items-center h-6 px-3 rounded-md border text-xs font-semibold capitalize ${getStatusPillClass(
                      tx?.status,
                    )}`}
                  >
                    {tx?.status || "—"}
                  </span>
                </div>
                <div className="mt-3 text-sm">
                  <div className="flex justify-between gap-3 py-1">
                    <div className="text-neutral-600">Amount</div>
                    <div className="tabular-nums">{money(tx?.amount)}</div>
                  </div>
                  <div className="flex justify-between gap-3 py-1">
                    <div className="text-neutral-600">Service fee</div>
                    <div className="tabular-nums">
                      {money(tx?.[serviceFeeField])}
                    </div>
                  </div>
                  <div className="flex justify-between gap-3 py-1">
                    <div className="text-neutral-600">{totalLabel}</div>
                    <div className="tabular-nums">
                      {money(tx?.[totalField])}
                    </div>
                  </div>
                  <div className="flex justify-between gap-3 py-1">
                    <div className="text-neutral-600">Payment date</div>
                    <div>
                      {tx?.payment_date ? DateFormat(tx?.payment_date) : "—"}
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <div className="mt-4 pt-4 border-t border-neutral-200">
                  <button
                    onClick={() => handleViewReceipt(tx)}
                    className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-[#0078ae] bg-[#0078ae]/10 hover:bg-[#0078ae]/20 rounded-lg transition-colors"
                    aria-label="View receipt"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    View Receipt
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Receipt Modal */}
      <ReceiptModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        transaction={selectedTransaction}
        mode={mode}
      />
    </>
  );
};
